/**
 * The per-document suggestion list in the AI panel (R1 W2), fed from the AI
 * edits store. An entry opens in place when its section is in the open editor
 * (the whole-document view), otherwise it navigates to that section's editor,
 * whose session load brings the review strip up.
 */

import { useInRouterContext, useNavigate } from 'react-router-dom'
import { UNTITLED_SECTION } from '@/lib/ai-edit-copy'
import { suggestionSectionPath, type PendingSuggestionItem } from '@/lib/ai-edit-view'
import { openPendingSuggestion } from '@/lib/ai-edit-actions'
import { useAiEditsStore } from '@/stores/ai-edits-store'
import { useAgentStore } from '@/stores/agent-store'
import { PendingSuggestionsList } from '@/components/drafting/PendingSuggestionsList'

// ===================
// Panel container — store-fed, opens in place or navigates
// ===================

function usePendingSuggestionItems(): PendingSuggestionItem[] {
  const suggestions = useAiEditsStore((s) => s.suggestions)
  const segments = useAgentStore((s) => s.segments)
  return suggestions.map((suggestion) => ({
    proposalId: suggestion.proposalId,
    sectionId: suggestion.sectionId,
    heading: suggestion.heading
      ?? segments.find((segment) => segment.sectionId === suggestion.sectionId)?.heading
      ?? UNTITLED_SECTION,
  }))
}

interface PanelPendingSuggestionsProps {
  bidId: string
  artifactId: string
  className?: string
}

export function PanelPendingSuggestions({ bidId, artifactId, className }: PanelPendingSuggestionsProps) {
  const items = usePendingSuggestionItems()
  const activeProposalId = useAgentStore((s) => s.draftPatchPreview?.proposalId ?? null)
  const inRouter = useInRouterContext()
  if (items.length === 0) return null
  const props = { items, activeProposalId, bidId, artifactId, ...(className ? { className } : {}) }
  return inRouter ? <RoutedSuggestionsList {...props} /> : <InPlaceSuggestionsList {...props} />
}

interface ContainerProps {
  items: PendingSuggestionItem[]
  activeProposalId: string | null
  bidId: string
  artifactId: string
  className?: string
}

function RoutedSuggestionsList({ items, activeProposalId, bidId, artifactId, className }: ContainerProps) {
  const navigate = useNavigate()
  const handleOpen = (item: PendingSuggestionItem) => {
    if (openPendingSuggestion(item.proposalId)) return
    void navigate(suggestionSectionPath(window.location.pathname, bidId, artifactId, item.sectionId))
  }
  return (
    <PendingSuggestionsList
      items={items}
      activeProposalId={activeProposalId}
      onOpen={handleOpen}
      {...(className ? { className } : {})}
    />
  )
}

/** Outside a router (tests, embedded previews) an entry can only open in place. */
function InPlaceSuggestionsList({ items, activeProposalId, className }: ContainerProps) {
  return (
    <PendingSuggestionsList
      items={items}
      activeProposalId={activeProposalId}
      onOpen={(item) => { openPendingSuggestion(item.proposalId) }}
      {...(className ? { className } : {})}
    />
  )
}
